import React, { useEffect, useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { useNavigate, useLocation } from 'react-router'

import TreeList, { Column, Selection, Scrolling, TreeListRef } from 'devextreme-react/tree-list'
import Tooltip from 'devextreme-react/tooltip'

import { DrawerState } from 'helpers/drawer-state-enum'

import { IxBadge } from 'components/badge'
import { RootState } from 'slices'

import _ from 'lodash'

import './drawer-tree-list.scss'

import { IIxNavItem } from 'routes'

import { useTranslation } from 'react-i18next'

interface IBadgeProps {
    path: string,
    iconBadge?: boolean
}
const Badge = ({ path, iconBadge=false }: IBadgeProps) => {
    const badgeValue = useSelector((state: RootState) => state.navBadgesSlice.badges[path])

    return (
        <IxBadge value={ badgeValue } iconBadge={ iconBadge } />
    )
}

interface IDrawerTreeListProps {
    data: IIxNavItem[],
    searchValue?: string,
    name?: string,
    setFavMenuItems?: any,
}

export const DrawerTreeList = ({ data, searchValue, name = 'menu', setFavMenuItems }: IDrawerTreeListProps) => {
    const treeList = useRef<TreeListRef>(null)
    const navigate = useNavigate()
    const location = useLocation()

    const firstRender = useRef<boolean>(true)
    const drawerState  = useSelector((state: RootState) => state.ui.drawerState)

    const [selectedRowsKeys, setSelectedRowsKeys] = useState<any[]>([])
    const loadLocation = useRef<boolean>(true)

    const { t } = useTranslation()

    useEffect(() => {
        /* v8 ignore next 3 */
        if (treeList.current && searchValue !== undefined) {
            treeList.current.instance()?.searchByText(searchValue)
        }
    }, [treeList, searchValue])

    useEffect(() => {
        if (!loadLocation.current) { loadLocation.current = true; return }
        const path = `${location.pathname}${location.search}`
        const findNavItem = (items: IIxNavItem[]): IIxNavItem | undefined => {
            let found = undefined
            _.forEach(items, (item: IIxNavItem) => {
                if (item.path.toLowerCase() === path.toLowerCase()) {
                    found = item
                    return false
                }
                if (item.items && item.items.length > 0) {
                    found = findNavItem(item.items)
                    if (found) { return false }
                }
            })

            return found
        }

        const navItem = findNavItem(data)
        setSelectedRowsKeys( navItem ? [navItem.fullPath] : [])
    }, [location, data])

    const cellClick = useCallback((navItem: any) => {
        if (navItem.path.startsWith('#')) {
            // todo: expand group
            treeList.current?.instance()?.deselectRows([navItem.path])
            if (treeList.current?.instance()?.isRowExpanded(navItem.fullPath)) {
                treeList.current?.instance()?.collapseRow(navItem.fullPath)
            } else {
                treeList.current?.instance()?.expandRow(navItem.fullPath)
            }
        } else {
            loadLocation.current = false
            navigate(navItem.path)
            setSelectedRowsKeys([navItem.fullPath])
        }
    }, [navigate])

    /* v8 ignore next */
    const emptyRender = useCallback((_: any) => null, [])

    // check if text is truncated and add tooltip
    const onMouseEnter = useCallback((e: any) => {
        if (drawerState !== DrawerState.Open) { return }

        const t = e.target
        if (t.scrollWidth > t.clientWidth) {
            t.title = t.innerText
        } else {
            t.title = ''
        }
    }, [drawerState])

    const cellRender = useCallback((cell: any) => {
        return (
            <div className='drawerMenu--item'
                id={`drawer-menu-item-${name}-${cell.rowIndex}`}
                data-testid='drawerMenu--item'
                key={cell.rowIndex}
                onClick={() => cellClick(cell.data)}>

                <div className='item-icon'>
                    <i className={ cell.data.icon.split(' ').length === 1 ? `dx-icon dx-icon-${cell.data.icon}` : `dx-icon ${cell.data.icon}` } />
                    <Badge path={cell.data.path} iconBadge={true} />
                </div>
                <div className='item-text'>
                    <span onMouseEnter={onMouseEnter}>
                        {t(cell.data.text)}
                    </span>
                    <Badge path={cell.data.path} />
                </div>
                {
                    drawerState === DrawerState.Compact
                        ? (
                            <Tooltip
                                target={`#drawer-menu-item-${name}-${cell.rowIndex}`}
                                position='right'
                                showEvent={{name: 'dxhoverstart', delay: 200}}
                                hideEvent='dxhoverend'
                                hideOnOutsideClick={false}
                                contentRender={() => (<p>{t(cell.data.text)}</p>)} />
                        )
                        : null
                }
            </div>
        )
    }, [cellClick, drawerState, name, onMouseEnter, t])

    useEffect(() => {
        setTimeout(() => {
            if (treeList.current && selectedRowsKeys.length && treeList.current.instance()?.getNodeByKey(selectedRowsKeys[0])) {
                treeList.current.instance()?.navigateToRow(selectedRowsKeys[0])
            }
        }, 0)
    }, [selectedRowsKeys])

    const onContentReady = useCallback(() => {
        if (treeList.current?.instance()?.getNodeByKey(`${location.pathname}${location.search}`) && firstRender.current) {
            treeList.current.instance()?.navigateToRow(`${location.pathname}${location.search}`)
            firstRender.current = false
        }
    }, [location])

    const onMoveFavorites = useCallback((pageName: string) => {
        const menuData = structuredClone(data)

        const findRow = (row: any) => {
            if (row.path === pageName) {
                return true
            }

            if (row.items) {
                const foundItem = row.items.find(findRow)
                if (foundItem) {
                    row.items = row.items.filter((item: any) => item.path === foundItem.path)
                }
                return foundItem
            }

            return false

        }

        const findFavRow = (row: any) => {
            if (row.path === pageName) { return true }
            if (row.items) { return row.items.find(findFavRow) }

            return false
        }

        const addFavItem = (foundBaseItem: any, favMenuItems: any) => {
            const favMenuData = structuredClone(favMenuItems)
            let row = null

            const findItem = (row: any): any => {
                if (row.path === pageName) {
                    return row
                }

                if (row.items?.length) {
                    for (const item of row.items) {
                        return findItem(item)
                    }
                }

                return null
            }

            const foundItem = findItem(foundBaseItem)
            const parentName = foundItem.fullPath.split('|').filter((path: string) => path !== foundItem.path).at(-1)

            const addFavRow = (row: any) => {
                if (row.items) {
                    row.items.find(addFavRow)
                }

                if (row.path === parentName) {
                    row.items = [...row.items, foundItem]
                }

                else {
                    if (row.path === foundBaseItem.path) {
                        !row.items.find((item: any) => item.path === foundBaseItem.items.find(findFavRow).path) && (row.items = [...row.items, foundBaseItem.items.find(findFavRow)])
                    }

                    if (row.path === foundBaseItem.items.find(findFavRow).path) {
                        !row.items.find((item: any) => item.path === parentName) && (row.items = [...row.items, ...foundBaseItem.items.find(findFavRow).items])
                    }
                }
            }

            row = {...foundBaseItem}

            if (foundBaseItem.items) {
                favMenuData.forEach(addFavRow)
                row = favMenuData.find((row: any) => row.path === foundBaseItem.path)
            }

            return row
        }

        const removeFavItem = (favMenuItems: any[]) => {

            const removeItem = (favMenuItems: any[]): any[] => {
                return favMenuItems
                    .filter((item: any) => item.path !== pageName)
                    .map((item: any) => ({...item, items: item.items ? removeItem(item.items) : item.items}))
            }

            const removeEmptyItems = (favMenuItems: any[]): any[] => {
                return favMenuItems
                .map(item => ({...item, items: item.items ? removeEmptyItems(item.items) : item.items}))
                .filter((item: any) => !item.items || item.items.length > 0)

            }

            return removeEmptyItems(removeItem(favMenuItems))
        }

        setFavMenuItems((favMenuItems: any) => {
            const foundBaseItem: any = menuData.find(findRow)
            const favItems = name === 'favMenu'
                ? removeFavItem(favMenuItems)
                : !favMenuItems.find(findFavRow)
                    ? favMenuItems.find((row: any) => row.path === foundBaseItem.path)
                        ? favMenuItems.map((item: any) => {return item.path === foundBaseItem.path ? addFavItem(foundBaseItem, favMenuItems) : item})
                        : [...favMenuItems, foundBaseItem]
                    : [...favMenuItems]

            return favItems
        })

    },[data, setFavMenuItems, name])

    const onContextMenuPreparing = useCallback((e: any) => {
        e.items = [{ icon: name === 'favMenu' ? 'minus' : 'plus', onClick: () => { onMoveFavorites(e.row.data.path) }, text: name === 'favMenu' ? t('global:button.removeFromFavorites') : t('global:button.addToFavorites') }]
    },[onMoveFavorites, name, t])

    return (
        <TreeList
            ref={treeList}
            dataSource={data}
            showColumnHeaders={false}
            showColumnLines={true}
            showBorders={false}
            showRowLines={false}
            columnAutoWidth={false}
            itemsExpr="items"
            dataStructure="tree"
            hoverStateEnabled={false}
            focusedRowEnabled={false}
            selection={ { mode: 'none' } }
            className='drawerMenu'
            onContentReady={ onContentReady }
            // width='100%'
            height='100%'
            repaintChangesOnly={true}
            noDataText='No matches'
            twoWayBindingEnabled={false}
            keyExpr='fullPath'
            selectedRowKeys={ selectedRowsKeys }
            autoNavigateToFocusedRow={true}
            onContextMenuPreparing={onContextMenuPreparing}
        >
            <Selection mode='single' />
            <Scrolling mode='standard' />

            <Column
                dataField='badge'
                allowSorting={false}
                allowSearch={false}
                cellRender={cellRender}
                width='100%'
            />
            <Column
                dataField='text'
                allowSearch={true}
                cellRender={emptyRender}
                width={0}
                calculateCellValue={ (rowData: any) => t(rowData.text) }
            />
            <Column
                dataField='selected'
                allowSearch={false}
                cellRender={emptyRender}
                width={0}
            />
        </TreeList>
    )
}
